import { useCallback, useMemo, useState } from "react";

export function useLeadSelection() {
  const [selectedLeads, setSelectedLeads] = useState([]);

  const lockedAccountId = selectedLeads[0]?.account?.id ?? null;
  const lockedAccountName = selectedLeads[0]?.account?.name ?? null;

  const isSelected = useCallback(
    (leadId) => selectedLeads.some((lead) => lead.id === leadId),
    [selectedLeads],
  );

  const canSelectLead = useCallback(
    (lead) => {
      if (selectedLeads.length === 0) return true;
      return (lead?.account?.id ?? null) === lockedAccountId;
    },
    [lockedAccountId, selectedLeads.length],
  );

  const toggleLead = useCallback((lead) => {
    if (!lead?.id) return;

    setSelectedLeads((previousSelection) => {
      const alreadySelected = previousSelection.some(
        (selectedLead) => selectedLead.id === lead.id,
      );

      if (alreadySelected) {
        return previousSelection.filter(
          (selectedLead) => selectedLead.id !== lead.id,
        );
      }

      const currentLockedAccountId = previousSelection[0]?.account?.id ?? null;
      const leadAccountId = lead?.account?.id ?? null;

      if (
        previousSelection.length > 0 &&
        leadAccountId !== currentLockedAccountId
      ) {
        return previousSelection;
      }

      return [...previousSelection, lead];
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedLeads([]);
  }, []);

  const removeLead = useCallback((leadId) => {
    setSelectedLeads((previousSelection) =>
      previousSelection.filter((selectedLead) => selectedLead.id !== leadId),
    );
  }, []);

  const selectedLeadIds = useMemo(
    () => selectedLeads.map((lead) => lead.id),
    [selectedLeads],
  );

  return {
    selectedLeads,
    selectedLeadIds,
    selectedCount: selectedLeads.length,
    lockedAccountId,
    lockedAccountName,
    isSelected,
    canSelectLead,
    toggleLead,
    clearSelection,
    removeLead,
  };
}
